import { skipToken } from '@reduxjs/toolkit/query';
import { RouteCodes } from '@strata/constants';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { Spinner } from '@strata/react-loading-animation';
import { ServicingRate } from '@strata/react-servicing-rate';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { remarketReferral } from 'api/agoraAPI';
import { ActionRateOverride } from 'components/referrals/dialogs/ActionRateOverride';
import { handleError } from 'services/Error';
import { useUpdateMarketplaceParticipantsToAllStateSuppliersMutation } from 'store/api/clientApi';
import { usePostDetermineReferralRatesQuery } from 'store/api/rateSetterApi';
import { fetchReferralDetails } from 'store/referrals/referralsSlice';

const Title = 'Update to Live Referral';

const ReasonOptions = ['No bid', 'Rate is out of market'];

export const UpdateToLiveReferralDialog = ({
    isOpen,
    onClose,
    referralDetails = undefined,
    memberDetails = undefined,
    username,
}) => {
    const dispatch = useDispatch();
    const {
        referralId,
        payerId,
        segmentId,
        productServiceOid,
        benefit,
        isBrand,
        memberId,
        patientFirstName,
        placeOfService,
    } = referralDetails ?? {};

    const { data: rateRule, isLoading } = usePostDetermineReferralRatesQuery(
        referralDetails && memberDetails
            ? {
                  payerId,
                  segmentId,
                  productServiceOid,
                  benefit,
                  isBrand,
                  memberId,
                  memberFirstName: patientFirstName,
                  memberDateOfBirth: memberDetails.dateOfBirth,
                  routeCode: RouteCodes.DEFAULT,
                  placeOfService,
                  lineOfBusiness: memberDetails.lineOfBusiness,
              }
            : skipToken,
    );

    const [updateMarketplaceParticipantsToAllStateSuppliers] =
        useUpdateMarketplaceParticipantsToAllStateSuppliersMutation();

    return (
        <Formik
            initialValues={{
                ...ActionRateOverride.initialValues,
                updateMarketplaceParticipants: false,
            }}
            width={560}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                ...ActionRateOverride.validationSchema,
                ...ReasonInput.validationSchema,
                // Only need reason when default rate override is defined, so override default reason schema
                reason: Yup.string().when('rateOverride', {
                    is: (rateOverride) => typeof rateOverride === 'number',
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                updateMarketplaceParticipants: Yup.bool(),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    const rateOverride = ActionRateOverride.submitValue(values);

                    if (values.updateMarketplaceParticipants) {
                        await updateMarketplaceParticipantsToAllStateSuppliers({
                            referralId,
                            payerId,
                        }).unwrap();
                    }

                    await remarketReferral({
                        referral_id: referralId,
                        payer_id: payerId,
                        segment_id: segmentId,
                        route_code: RouteCodes.DEFAULT,
                        default_rate: rateOverride,
                        ...(typeof rateOverride === 'number'
                            ? ReasonInput.submitValues(values)
                            : undefined),
                        username,
                    });

                    await dispatch(fetchReferralDetails({ payerId, referralId }));

                    SuccessToast({ message: 'Updated to Live Referral successfully.' });

                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update to Live Referral. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update To Live Referral"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        isLoading ? (
                            <Spinner className="padding__around--large" size={Spinner.Size.LARGE} />
                        ) : (
                            <>
                                <DetailLabelValuePair
                                    className="margin__bottom--medium"
                                    label={`${rateRule?.referentPriceType ?? ''} Default Rate`}
                                    value={
                                        <ServicingRate
                                            servicingRate={rateRule?.defaultRate}
                                            referentPriceType={rateRule?.referentPriceType}
                                        />
                                    }
                                />
                                <ActionRateOverride
                                    label="Default Rate Override"
                                    rateRule={rateRule}
                                />
                                {typeof formik.values.rateOverride === 'number' && (
                                    <ReasonInput reasons={ReasonOptions} />
                                )}
                                <Checkbox
                                    name="updateMarketplaceParticipants"
                                    label="Update participants to suppliers that service all 50 states"
                                    checked={formik.values.updateMarketplaceParticipants}
                                    onChange={(checked) =>
                                        formik.setFieldValue(
                                            'updateMarketplaceParticipants',
                                            checked,
                                        )
                                    }
                                />
                            </>
                        )
                    }
                />
            )}
        </Formik>
    );
};

UpdateToLiveReferralDialog.Title = Title;

UpdateToLiveReferralDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
        productServiceOid: PropTypes.string.isRequired,
        benefit: PropTypes.string.isRequired,
        isBrand: PropTypes.bool.isRequired,
        memberId: PropTypes.string.isRequired,
        patientFirstName: PropTypes.string.isRequired,
        placeOfService: PropTypes.string.isRequired,
    }),
    memberDetails: PropTypes.shape({
        dateOfBirth: PropTypes.string.isRequired,
    }),
    username: PropTypes.string.isRequired,
};
